import { Skeleton } from '@gravity-ui/uikit';
import { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { PlayerSizeChangeEvent } from '../../../types/player/PlayerEvents';

type PlayerParams = {
    loading?: boolean;
    playerUrl: string;
    onVideoSizeChange?: (width: number, height: number) => void;
};

export interface PlayerHandle {
    seekTo: (timeInSeconds: number) => void;
}

export const Player = forwardRef<PlayerHandle, PlayerParams>(
    ({ loading, playerUrl, onVideoSizeChange }, ref) => {
        const iframeRef = useRef<HTMLIFrameElement>(null);

        const initedEventHandler = (message: MessageEvent): void => {
            let playerEvent;

            try {
                playerEvent = JSON.parse(message.data);
            } catch (e) {
                playerEvent = message.data;
            }

            if (playerEvent.event === 'sizeChange') {
                const sizeChangeEvent = playerEvent as PlayerSizeChangeEvent;
                if (
                    onVideoSizeChange &&
                    sizeChangeEvent.videoWidth &&
                    sizeChangeEvent.videoHeight
                ) {
                    onVideoSizeChange(sizeChangeEvent.videoWidth, sizeChangeEvent.videoHeight);
                }
            }
            if (playerEvent.event === 'inited') {
                setPlayerInited(true);
            }
        };

        const [playerInited, setPlayerInited] = useState<boolean>(false);

        useEffect(() => {
            window.addEventListener('message', initedEventHandler);

            return () => {
                window.removeEventListener('message', initedEventHandler);
            };
        }, [onVideoSizeChange]);

        const seekTo = (timeInSeconds: number) => {
            if (iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.postMessage(
                    JSON.stringify({ method: 'seek', time: timeInSeconds }),
                    '*',
                );
            }
        };

        useImperativeHandle(ref, () => ({
            seekTo,
        }));

        return (
            <>
                {(loading || !playerInited) && (
                    <Skeleton
                        style={{
                            width: '100%',
                            height: '211px',
                            borderRadius: '0px',
                        }}
                    />
                )}
                {!loading && (
                    <div
                        style={{
                            minWidth: '100%',
                            display: playerInited ? 'block' : 'none',
                        }}
                    >
                        <div
                            style={{
                                position: 'relative',
                                paddingBottom: '56.25%',
                                height: 0,
                            }}
                        >
                            <iframe
                                ref={iframeRef}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                }}
                                src={playerUrl}
                                allow="autoplay; fullscreen; accelerometer; gyroscope; picture-in-picture; encrypted-media"
                                frameBorder="0"
                                scrolling="no"
                            ></iframe>
                        </div>
                    </div>
                )}
            </>
        );
    },
);
